import { useState } from "react";
import API from "../lib/api";
import { downloadAuthFile } from "../lib/fileOpen";
import { Button } from "./ui/button";
import { SealCheck, DownloadSimple, BellRinging, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

/* Admin view of the client's final sign-off on a completed job (or the nudge to request it). */
export default function ClientSignOffPanel({ job, onChanged }) {
  const [busy, setBusy] = useState(false);
  const rec = job.client_sign_off;
  if (!rec && job.status !== "completed") return null;

  const requestSignOff = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/jobs/${job.id}/request-sign-off`);
      toast.success(data.message);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send request"); } finally { setBusy(false); }
  };

  if (!rec) {
    return (
      <div className="border border-dashed border-amber-300 dark:border-amber-800 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="job-sign-off-pending">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><SealCheck size={12} /> Client sign-off</h3>
          <p className="text-xs mt-1">Awaiting the client's final sign-off in the portal.{job.sign_off_requested_at && <span className="text-muted-foreground"> Requested {fmt(job.sign_off_requested_at)}{job.sign_off_requested_by ? ` by ${job.sign_off_requested_by}` : ""}.</span>}</p>
        </div>
        {job.client_id && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={requestSignOff} disabled={busy} data-testid="job-request-sign-off-btn">
            <BellRinging size={12} className="mr-1" /> {job.sign_off_requested_at ? "Remind client" : "Request sign-off"}
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/20 rounded-md p-4" data-testid="job-sign-off-done">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5"><SealCheck size={12} weight="fill" /> Client signed off</h3>
          <p className="text-sm font-medium mt-1">{rec.typed_name} <span className="text-xs text-muted-foreground">({rec.client_name})</span></p>
          <p className="text-[11px] text-muted-foreground">{fmt(rec.signed_at)} · via client portal · code <span className="font-mono">{rec.verification_code}</span></p>
          {rec.rating && <p className="text-[11px] mt-1 inline-flex items-center gap-0.5" data-testid="job-sign-off-rating">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={11} weight={rec.rating >= n ? "fill" : "regular"} className={rec.rating >= n ? "text-amber-400" : "text-muted-foreground/40"} />)} <span className="ml-1 text-muted-foreground">{rec.rating}/5</span></p>}
          {rec.comment && <p className="text-xs italic mt-1.5">“{rec.comment}”</p>}
          <p className="text-[10px] text-muted-foreground mt-1">Reviewed {rec.reviewed?.photos || 0} photo(s), {rec.reviewed?.documents || 0} document(s), {rec.reviewed?.forms || 0} form(s)</p>
        </div>
        <img src={rec.signature} alt="Client signature" className="h-14 bg-white rounded border border-border px-2" data-testid="job-sign-off-signature" />
      </div>
      <Button size="sm" variant="outline" className="mt-3 h-7 text-xs" onClick={() => downloadAuthFile(`/api/jobs/${job.id}/sign-off-certificate`, `${job.reference}_sign_off_certificate.pdf`)} data-testid="job-sign-off-certificate-btn">
        <DownloadSimple size={12} className="mr-1" /> Sign-off certificate (PDF)
      </Button>
    </div>
  );
}
